// Ticket 5 ("Route lines: three distinct states, round caps and curves"), transport-lines.md
// "Curved lines". Segments bend alternately left and right so a multi-leg route reads as a route
// rather than a zigzag — quadratic Bézier, perpendicular control point, 24 segments. This is a
// generated LineString, not a Mapbox setting: it bends in lon/lat rather than projected space, so the
// visual bend varies slightly with latitude (invisible at trip-sized segments — do not reuse this for
// anything needing geometric accuracy).
export type LonLat = [number, number];

/** One curved segment between `a` and `b`, bowed perpendicular to the line by `bend` (a fraction of
 * the segment's own length). Positive/negative flips which side it bows to. */
export function arc(a: LonLat, b: LonLat, bend: number): LonLat[] {
	const mx = (a[0] + b[0]) / 2;
	const my = (a[1] + b[1]) / 2;
	const dx = b[0] - a[0];
	const dy = b[1] - a[1];
	const cx = mx - dy * bend;
	const cy = my + dx * bend;
	const pts: LonLat[] = [];
	for (let t = 0; t <= 1.0001; t += 1 / 24) {
		const u = 1 - t;
		pts.push([u * u * a[0] + 2 * u * t * cx + t * t * b[0], u * u * a[1] + 2 * u * t * cy + t * t * b[1]]);
	}
	return pts;
}

/**
 * `b` moved by 360° of longitude when that is the short way from `a`. A leg across the antimeridian
 * (Tokyo +139.7 to Honolulu −157.8) otherwise interpolates through 0° and is drawn the long way round
 * the globe instead of across the Pacific. Mapbox draws longitudes past ±180 as the same place, so the
 * line simply crosses the date line. Same rule as the trip guide's static map (pdfexport/guide/staticMap.ts).
 */
export function shortWayRound(a: LonLat, b: LonLat): LonLat {
	const d = b[0] - a[0];
	if (d > 180) return [b[0] - 360, b[1]];
	if (d < -180) return [b[0] + 360, b[1]];
	return b;
}

/**
 * The same points with western longitudes moved by +360 when that makes their box narrower: a trip
 * across the Pacific (Japan, Hawaii) would otherwise get a box spanning the whole world and the map
 * zooms out to the globe. Only when it is narrower, so a trip round the world keeps its own box.
 * Same rule as the trip guide's static map (pdfexport/guide/staticMap.ts).
 */
export function acrossDateLine(points: LonLat[]): LonLat[] {
	if (points.length < 2) return points;
	const span = (pts: LonLat[]) => Math.max(...pts.map((p) => p[0])) - Math.min(...pts.map((p) => p[0]));
	const shifted = points.map(([lng, lat]) => [lng < 0 ? lng + 360 : lng, lat] as LonLat);
	return span(shifted) < span(points) ? shifted : points;
}

// 0.10 of the segment length — the offset used in the prototype.
const BEND = 0.1;

/**
 * A curved LineString feature between two points, alternating bend direction by `index` — the
 * alternation is the point (transport-lines.md: "A constant sign makes a multi-leg route curve away
 * in one direction like a spiral"). `totalSegments <= 1` (a single-leg trip) stays straight: a curve
 * there reads as an error, not a route.
 */
export function curvedOrStraightLine(a: LonLat, rawB: LonLat, index: number, totalSegments: number): GeoJSON.Feature<GeoJSON.LineString> {
	const b = shortWayRound(a, rawB);
	const coordinates = totalSegments > 1 ? arc(a, b, index % 2 ? BEND : -BEND) : [a, b];
	return {
		type: "Feature",
		properties: {},
		geometry: { type: "LineString", coordinates },
	};
}
